import { DryRunFrame } from '@/components/admin/experiences/dry-run-frame';
import { ExperienceSaveBlockers } from '@/components/admin/experiences/experience-blockers';
import {
  ExperienceVariableScope,
  useExperienceVariableScope,
} from '@/components/admin/experiences/experience-variable-scope';
import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import {
  PageEditorBody,
  type PageEditorBodyProps,
} from '@/components/admin/experiences/page-editor/page-editor-body';
import { Segmented } from '@/components/admin/experiences/page-editor/segmented';
import { ExperiencePreviewButton } from '@/components/admin/experiences/preview/preview-button';
import { ProtocolReadingLink } from '@/components/admin/experiences/protocol-reading-link';
import type { SaveBoxHandle } from '@/components/admin/versioned/versioned-editor';
import { VersionedEditor } from '@/components/admin/versioned/versioned-editor';
import { useAdminSession } from '@/lib/admin-session';
import {
  checkExperience,
  experiencesApi,
  fetchAmbianceAssets,
  fetchProtocols,
  uploadAmbianceAsset,
} from '@/lib/api';
import type { ExperienceDraft } from '@/lib/experience-draft';
import { EXPERIENCE_DRAFT_KIT } from '@/lib/experience-draft';
import { experienceRefusalSentence } from '@/lib/experience-labels';
import { experienceContrastWarnings } from '@/lib/experience-theme';
import { EXPERIENCE_WORDING } from '@/lib/experience-wording';
import { DEFAULT_NOYAU_PREVIEW_PAIR, type NoyauPreviewPair } from '@/lib/noyau-preview';
import { pageIndexOf, pageOf } from '@/lib/page-editor';
import { useNoyauTextsSource } from '@/lib/use-noyau-texts';
import { previewVariableNames, variableNamer } from '@/lib/variable-pill';
import type {
  ExperienceContent,
  ExperienceDetail,
  ExperienceDraftContent,
  VersionedRef,
} from '@matter_extractor/shared';
import { Alert, Icon, buttonVariants, cn } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * L'éditeur d'UNE page, dans le logement `solo` de la couche versionnée
 * (refonte modulaire §9.1, §9.2, §9.3; planches `EditeurPageEcran` et
 * `EditeurPageTelephone`; issue #58).
 *
 * §9.1: « l'éditeur d'une page s'ouvre depuis l'onglet Timeline et le remplace :
 * il occupe l'écran seul, sans les trois onglets, sous un fil d'Ariane et un
 * retour Timeline ». It is nonetheless the EXPERIENCE's editor - the same
 * entity, the same draft, the same autosave, the same save box - so it mounts
 * the same layer with the same six parameters, in its fourth logement.
 *
 * ## Le brouillon de l'expérience contient toutes ses pages
 *
 * There is one draft and one version. So this screen edits ONE page of it
 * (`withPage`) and saves through `EXPERIENCE_DRAFT_KIT`, which means an
 * autosave fired here carries the whole timeline - exactly what the Timeline
 * tab's own autosave carries. Nothing is scoped, nothing is merged, and a
 * version saved from here is the version the other screen would have saved.
 *
 * ## « Créer un lien » ne suit pas, et l'Historique non plus
 *
 * Both are gestures of the EXPERIENCE, one click away through the breadcrumb
 * (maquette README: « c'est un geste de l'expérience, on le retrouve en
 * remontant »). The solo logement renders neither, which is what it is for.
 *
 * ## Ce que la palette `@` lit, et quand
 *
 * §8.6 gives four kinds of variable and two of them belong to the PROTOCOL the
 * protocol page carries. So this screen reads that protocol - and only when
 * there is one - plus the criteria library, because a criterion is named by its
 * own entity and not by the protocol that cites it. An unread library leaves a
 * name blank rather than guessing, the régime `protocol-editor.tsx` holds. The
 * reads live in `ExperienceVariableScope` since the grill Variables (#88): the
 * pills of every zone, the menu and the previews share them through one scope.
 *
 * ## Ce que la SECONDE MR de #58 a posé, et où
 *
 * Three pieces of §9.3, §9.4 and §9.5 reuse what issue #57 built, so they
 * landed once that lot was merged, and all three live in `PageEditorBody`: the
 * folded « Visuel de la page » bar between the navigation banner and the three
 * zones, the two « Insérer une page avant / après » of the banner
 * (`page-nav.tsx`), and the protocol page's own selector
 * (`protocol-page-panel.tsx`, which mounts the card and the picker of #57
 * rather than drawing a second protocol).
 *
 * What is THIS file's share of them: the protocol library they read, on the
 * protocol page alone, and the contrast of §9.4 measured page by page in the
 * save box.
 */

export interface PageEditorProps {
  readonly detail: ExperienceDetail;
  readonly pageId: string;
  readonly onGoToPage: (pageId: string) => void;
  readonly onReload: () => void;
}

export function PageEditor({ detail, pageId, onGoToPage, onReload }: PageEditorProps) {
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  const [format, setFormat] = useState<'phone' | 'screen'>('screen');
  /*
    « Aperçu avec » (noyau.md N20): posture of the editor, never content, held
    HERE - above the key that remounts the body on every page - so the pair an
    operator picked stays across pages and opens the full-screen preview.
  */
  const [noyauPair, setNoyauPair] = useState<NoyauPreviewPair>(DEFAULT_NOYAU_PREVIEW_PAIR);
  const [saving, setSaving] = useState<SaveBoxHandle<ExperienceContent> | null>(null);
  /** Each opening reads its own dry run, never the last one's (`experience-editor.tsx`). */
  const [openings, setOpenings] = useState(0);

  /**
   * The dry run of §9.1, fired ONCE per opening - the régime of #55 - and read
   * at the gesture, never from the cache: the régime `experience-editor.tsx`
   * states for both its dry runs (N36, review of !474).
   */
  const check = useQuery({
    queryKey: ['experience-check', email, detail.id, openings, saving?.content ?? null],
    queryFn: () => {
      if (saving === null) throw new Error('no content to check');
      return checkExperience({ content: saving.content, alsoProduction: true });
    },
    enabled: saving !== null,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
  });

  const assets = useQuery({
    queryKey: ['ambiance-assets', email],
    queryFn: fetchAmbianceAssets,
    retry: false,
  });

  const upload = useMutation({
    mutationFn: (input: { file: File; license: string; tags: string }) =>
      uploadAmbianceAsset(input.file, input.license, input.tags),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ambiance-assets', email] });
    },
  });

  /*
    Les pages TELLES QUE LE SERVEUR LES TIENT : le brouillon quand il y en a un,
    la version `développement` sinon - l'ordre d'`openingDraft`.

    Read off the record and not off the local draft, because what is read here
    are REFERENCES, not fields, and the record is what the save box is about.
    The zones of the body read the protocol off the DRAFT instead, through
    `ExperienceVariableScope`, so choosing a protocol on the protocol page moves
    the `@` palette at once; this copy names the variables of a refused line,
    which the versioned layer renders outside that scope.
  */
  const pagesOnRecord: readonly {
    readonly id: string;
    readonly kind: string;
    readonly protocol: VersionedRef | null;
  }[] = detail.draft?.content.pages ?? detail.developmentContent.pages;

  /** The protocol the PROTOCOL PAGE carries, for the names of the save box (§8.6). */
  const protocolRef = pagesOnRecord.find((page) => page.kind === 'protocol')?.protocol ?? null;
  const recordScope = useExperienceVariableScope(protocolRef, detail.id);

  /** Whether THIS page is the protocol page - what decides the surface of §9.5. */
  const editingProtocolPage = pagesOnRecord.some(
    (page) => page.id === pageId && page.kind === 'protocol',
  );

  /*
    La bibliothèque de §9.5, et seulement sur la page protocole.

    `['protocols', email]` is deliberately the key `experience-timeline-tab.tsx`
    already writes - same fetcher, same schema, same shape - so the card drawn
    here and the card drawn on the scene read ONE list. It is `enabled` on the
    protocol page alone because no other page can show a protocol: reading it
    everywhere would be one request per page opened, for a selector nobody can
    reach from there.
  */
  const protocols = useQuery({
    queryKey: ['protocols', email],
    queryFn: fetchProtocols,
    enabled: editingProtocolPage,
    retry: false,
  });

  return (
    <VersionedEditor<ExperienceContent, ExperienceDraftContent, ExperienceDraft>
      wording={EXPERIENCE_WORDING}
      api={experiencesApi}
      kit={EXPERIENCE_DRAFT_KIT}
      keys={{ detail: 'experience', list: 'experiences' }}
      detail={detail}
      viewing={null}
      onCloseView={() => undefined}
      onView={() => undefined}
      onReload={onReload}
      onCreated={() => undefined}
      layout="solo"
      /*
        A function of the binding and not a node: the last segment names the
        page, which somebody renames in the « Page » panel - read off `detail`
        it would have lagged the field beside it by one autosave.
      */
      breadcrumb={(binding) => (
        <p className="flex flex-wrap items-center gap-x-2 text-gray-600 text-small">
          <Link to="/admin/experiences" className="text-bleu-nuit underline underline-offset-2">
            Expériences
          </Link>
          <span aria-hidden>›</span>
          <Link
            to="/admin/experiences/$experienceId"
            params={{ experienceId: detail.id }}
            search={{ tab: 'timeline', page: pageId }}
            className="text-bleu-nuit underline underline-offset-2"
          >
            {binding.draft.name}
          </Link>
          <span aria-hidden>›</span>
          <span>{pageOf(binding.draft, pageId)?.name || 'Page sans nom'}</span>
          <Link
            to="/admin/experiences/$experienceId"
            params={{ experienceId: detail.id }}
            search={{ tab: 'timeline', page: pageId }}
            className={cn(buttonVariants({ variant: 'accent', size: 'sm' }), 'ml-2 no-underline')}
          >
            <Icon name="arrow_back" size={16} />
            Timeline
          </Link>
        </p>
      )}
      /*
        The `h1` names the PAGE and the line under it names the experience
        (maquette `EditeurPageEcran`). A function of the binding because the
        page's name lives in the draft and moves as somebody types it.
      */
      heading={(binding) => {
        const page = binding.draft.pages.find((one) => one.id === pageId) ?? null;
        const index = pageIndexOf(binding.draft, pageId);
        return {
          title: page === null || page.name === '' ? 'Page sans nom' : page.name,
          subtitle: (
            <>
              Page {index + 1} de « {binding.draft.name} »
            </>
          ),
        };
      }}
      /*
        La bascule Téléphone | Écran, à gauche du bouton d'enregistrement (§9.3),
        et la porte de l'aperçu plein écran juste à côté (#141). La porte NOMME
        ses variables depuis la portée du RECORD, comme la boîte d'enregistrement
        à côté d'elle : l'en-tête est rendu par la couche versionnée, donc hors
        du `ExperienceVariableScope` que les zones montent plus bas.
      */
      renderHeaderExtra={(draft) => (
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            label="Format de l’aperçu"
            testId={PAGE_EDITOR_IDS.format}
            value={format}
            options={[
              { value: 'phone', label: 'Téléphone' },
              { value: 'screen', label: 'Écran' },
            ]}
            readOnly={false}
            onChange={setFormat}
          />
          <ExperiencePreviewButton
            draft={draft}
            pageId={pageId}
            format={format}
            variableNames={previewVariableNames({ draft, ...recordScope })}
            noyauPair={noyauPair}
          />
        </div>
      )}
      onSaveBox={(opened) => {
        setSaving(opened);
        if (opened !== null) setOpenings((count) => count + 1);
      }}
      renderSaveBlockers={(draft, alsoProduction) => {
        // Nothing claimed before the first answer; the previous one, greyed and
        // still blocking, while it is read again (`DryRunFrame`).
        const answer = check.data;
        if (answer === undefined) return null;
        const rechecking = check.isFetching;
        const blockers = alsoProduction ? answer.blockers : [];
        const refusals = answer.problems.length + blockers.length;
        /*
          Le contraste de §9.4, sur le thème ET sur chaque page qui le surcharge.

          The same function as the shell's box, and the same list: both record
          the WHOLE experience, so a box shortened to the page on screen would
          hide a pairing the other one names. A page that overrides nothing adds
          nothing - `pageContrastWarnings` filters on the sentence - so an
          experience that never touched a page's visual reads exactly as it did
          before this lot.
        */
        const contrast = experienceContrastWarnings(draft.theme, draft.pages);
        const said = refusals > 0 || answer.warnings.length > 0 || contrast.length > 0;
        if (!said && !rechecking) return null;
        return {
          content: (
            <DryRunFrame rechecking={rechecking}>
              <ExperienceSaveBlockers
                problems={answer.problems}
                blockers={blockers}
                warnings={answer.warnings}
                contrast={contrast}
                nameOf={variableNamer({ draft, ...recordScope })}
                pages={draft.pages}
                onOpen={(place) => {
                  saving?.close();
                  if (place.pageId !== null) onGoToPage(place.pageId);
                }}
                /*
                  « Ouvrir le protocole » here too (noyau.md §3.7, review of
                  !474): this box saves the same version as the shell's, so it
                  says the same lines with the same door - a protocol without
                  reading and, since #158, an orphan direction, each opening
                  the view the request names - closed through ITS `leave`,
                  which writes the autosave still owed before the page editor
                  unmounts. The experience exists, so never a new tab.
                */
                renderProtocolLink={(request) => (
                  <ProtocolReadingLink {...request} newTab={false} onLeave={saving?.leave} />
                )}
              />
            </DryRunFrame>
          ),
          blocked: refusals > 0,
        };
      }}
      ownRefusal={experienceRefusalSentence}
      renderFields={(binding) => (
        <section data-testid={PAGE_EDITOR_IDS.root} className="space-y-4">
          {upload.isError ? (
            <Alert tone="danger">
              Le fichier n’a pas pu être téléversé. Vérifie son format et sa taille, puis réessaie.
            </Alert>
          ) : null}

          {/*
            La `key` que la ROUTE portait avant la seconde MR de #58.

            Changer de page doit remettre à zéro la sélection, l'onglet de
            l'inspecteur et les valeurs de répétition de l'aperçu : tout cet
            état-là vit ici, donc c'est ici que le remontage appartient. Sur la
            route, il emportait aussi le BROUILLON, qui se resemait alors depuis
            le serveur - et une page qu'on venait d'insérer disparaissait dans
            la seconde qui séparait la navigation de l'autosauvegarde débouncée.
          */}
          <ExperienceVariableScope draft={binding.draft} experienceId={detail.id}>
            <PageEditorBodyWithTexts
              key={pageId}
              draft={binding.draft}
              pageId={pageId}
              readOnly={binding.readOnly}
              format={format}
              assets={assets.data?.assets ?? []}
              protocols={protocols.data?.protocols ?? []}
              protocolsRead={protocols.data !== undefined}
              onDraft={binding.onDraft}
              onGoToPage={onGoToPage}
              uploading={upload.isPending}
              onUpload={(file, license, tags) => upload.mutate({ file, license, tags })}
              noyauPair={noyauPair}
              onNoyauPair={setNoyauPair}
            />
          </ExperienceVariableScope>
        </section>
      )}
    />
  );
}

/**
 * The body of the page, with the texts of the noyaux it reads (noyau.md §3.5,
 * N20; vitrine.md §3.1): read HERE, at the pin the DRAFT holds, and handed to
 * the body like the protocol library above - the inspector of a Noyau block
 * names their versions and the preview renders their real texts. Read whatever
 * the page, because the Noyau block an operator is about to add must show its
 * texts at once - not one autosave later - and at the draft's pin, because the
 * pill-menu of the inspector moves it before any save.
 */
function PageEditorBodyWithTexts(props: Omit<PageEditorBodyProps, 'noyauTexts'>) {
  const noyauTexts = useNoyauTextsSource(true, props.draft.noyauTexts?.pin ?? 'latest');
  return <PageEditorBody {...props} noyauTexts={noyauTexts} />;
}
